import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  ArrowUpRight,
  CheckCircle2,
  Circle,
  Loader2,
  Printer,
  Mail,
  RotateCcw,
} from "lucide-react";
import { toast } from "sonner";
import PageHero from "../components/PageHero";
import { CHECKLIST_SECTIONS } from "../lib/catalog";
import { CALENDLY_URL } from "../lib/site";
import { useClientAuth } from "../lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "tinkershift.checklist.v1";

const ALL_ITEMS = CHECKLIST_SECTIONS.flatMap((s) => s.items);

export default function ChecklistPage() {
  const { client } = useClientAuth();
  const navigate = useNavigate();
  const [checked, setChecked] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", organisation: "", role: "", notes: "" });

  // Prefill contact details from the signed-in client profile
  useEffect(() => {
    if (client) {
      setForm((f) => ({
        ...f,
        name: f.name || client.name || "",
        email: f.email || client.email || "",
        organisation: f.organisation || client.organisation || "",
      }));
    }
  }, [client]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(checked));
    } catch {
      /* localStorage disabled — fine */
    }
  }, [checked]);

  const toggle = (item) =>
    setChecked((prev) => ({ ...prev, [item]: !prev[item] }));

  const haveCount = useMemo(
    () => ALL_ITEMS.filter((i) => checked[i]).length,
    [checked],
  );
  const total = ALL_ITEMS.length;
  const missingCount = total - haveCount;
  const pct = Math.round((haveCount / total) * 100);

  const reset = () => {
    setChecked({});
    toast.success("Checklist reset.");
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/checklist`,
        {
          ...form,
          have_items: ALL_ITEMS.filter((i) => checked[i]),
          missing_items: ALL_ITEMS.filter((i) => !checked[i]),
        },
        { withCredentials: true },
      );
      setSent(true);
      toast.success("Checklist sent — we’ll be in touch.");
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Your session expired — please sign in again.");
        navigate(`/login?next=${encodeURIComponent("/checklist")}`);
      } else {
        toast.error("Couldn’t send right now. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="checklist-page">
      <PageHero
        eyebrow="Inventory Checklist"
        accent="#FF6600"
        title={
          <>
            See exactly what you{" "}
            <span className="bg-[#FFD700] px-2 inline-block">have</span> — and
            what you’re missing.
          </>
        }
        intro="Tick off what’s already in your space. Progress is saved to your device. When you’re done, send it to Julija and we’ll help close the gaps."
      />

      {/* Progress strip */}
      <section className="bg-white border-b-2 border-[#1A1A1A] sticky top-16 md:top-20 z-30 backdrop-blur-md bg-white/90">
        <div className="mx-auto max-w-7xl px-5 md:px-10 lg:px-12 py-5 flex items-center gap-6 flex-wrap">
          <div className="flex-1 min-w-[220px]">
            <div className="flex items-baseline justify-between gap-4">
              <p className="font-mono text-[11px] uppercase tracking-widest text-[#4A4A4A]">
                Your space · progress
              </p>
              <p
                className="font-display font-black text-lg tracking-tight"
                data-testid="checklist-progress-count"
              >
                {haveCount} / {total} items · {pct}%
              </p>
            </div>
            <div className="mt-2 h-3 w-full border-2 border-[#1A1A1A] rounded-full overflow-hidden bg-[#F0F0F0]">
              <motion.div
                data-testid="checklist-progress-bar"
                className="h-full bg-[#00C2B8] min-w-[2px]"
                initial={false}
                animate={{ width: `${Math.max(pct, 0.5)}%` }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              />
            </div>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={reset}
              data-testid="checklist-reset-btn"
              className="inline-flex items-center gap-2 bg-white text-[#1A1A1A] border-2 border-[#1A1A1A] rounded-full px-4 py-2 text-xs font-bold uppercase tracking-widest nb-shadow-sm nb-press"
            >
              <RotateCcw size={13} strokeWidth={2.5} /> Reset
            </button>
            <button
              type="button"
              onClick={() => window.print()}
              data-testid="checklist-print-btn"
              className="inline-flex items-center gap-2 bg-white text-[#1A1A1A] border-2 border-[#1A1A1A] rounded-full px-4 py-2 text-xs font-bold uppercase tracking-widest nb-shadow-sm nb-press"
            >
              <Printer size={13} strokeWidth={2.5} /> Print
            </button>
          </div>
        </div>
      </section>

      {/* Checklist sections */}
      <section className="bg-[#F8F9FA] border-b-2 border-[#1A1A1A]">
        <div className="mx-auto max-w-7xl px-5 md:px-10 lg:px-12 py-14 md:py-20 space-y-10">
          {CHECKLIST_SECTIONS.map((section, i) => {
            const sectionHave = section.items.filter((it) => checked[it]).length;
            return (
              <motion.div
                key={section.key}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.7, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`checklist-section-${section.key}`}
              >
                <div className="flex items-baseline justify-between flex-wrap gap-3">
                  <h2 className="font-display font-black text-2xl md:text-3xl tracking-tighter">
                    <span className="font-mono text-xs uppercase tracking-widest text-[#4A4A4A] mr-3">
                      0{i + 1}
                    </span>
                    {section.name}
                  </h2>
                  <span className="font-mono text-[11px] uppercase tracking-widest text-[#4A4A4A]">
                    {sectionHave} / {section.items.length}
                  </span>
                </div>
                <ul className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3">
                  {section.items.map((item) => {
                    const isChecked = !!checked[item];
                    return (
                      <li key={item}>
                        <button
                          type="button"
                          onClick={() => toggle(item)}
                          data-testid={`checklist-item-${item.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                          aria-pressed={isChecked}
                          className={`w-full text-left flex items-start gap-3 border-2 border-[#1A1A1A] rounded-2xl p-4 nb-shadow-sm transition-all ${
                            isChecked
                              ? "bg-[#00C2B8] text-[#1A1A1A]"
                              : "bg-white text-[#1A1A1A] hover:bg-[#FFD700]/30"
                          }`}
                        >
                          {isChecked ? (
                            <CheckCircle2 size={20} strokeWidth={2.5} className="flex-shrink-0 mt-0.5" />
                          ) : (
                            <Circle size={20} strokeWidth={2.5} className="flex-shrink-0 mt-0.5 opacity-50" />
                          )}
                          <span className="font-semibold">{item}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Send to Julija */}
      <section
        data-testid="checklist-submit-section"
        className="bg-[#1A1A1A] text-white border-b-2 border-[#1A1A1A] relative overflow-hidden"
      >
        <div className="absolute inset-0 bg-dots-light opacity-20 pointer-events-none" />
        <div className="relative mx-auto max-w-5xl px-5 md:px-10 lg:px-12 py-16 md:py-20">
          {!sent ? (
            <>
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#FFD700] font-bold">
                Step 02 · send your results
              </p>
              <h2 className="mt-3 font-display font-black text-3xl md:text-5xl leading-[1.02] tracking-tighter max-w-3xl">
                Send your checklist to Julija.
              </h2>
              <p className="mt-4 text-white/80 max-w-2xl">
                You’ll receive a tailored response within 1–2 business days with
                recommendations, approximate costs and a path to a full
                makerspace plan.
              </p>
              <form
                onSubmit={submit}
                data-testid="checklist-form"
                className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4"
              >
                <input
                  required
                  placeholder="Name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="checklist-name-input"
                  className="form-dark"
                />
                <input
                  required
                  placeholder="Organisation"
                  value={form.organisation}
                  onChange={(e) => setForm({ ...form, organisation: e.target.value })}
                  data-testid="checklist-org-input"
                  className="form-dark"
                />
                <input
                  placeholder="Role (optional)"
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value })}
                  data-testid="checklist-role-input"
                  className="form-dark"
                />
                <input
                  required
                  type="email"
                  placeholder="Email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="checklist-email-input"
                  className="form-dark"
                />
                <textarea
                  rows={3}
                  placeholder="Anything we should know? (budget, timeline, year levels…)"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  data-testid="checklist-notes-input"
                  className="form-dark md:col-span-2 resize-y"
                />
                <div className="md:col-span-2 flex flex-wrap gap-3 items-center">
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="checklist-submit-btn"
                    className="inline-flex items-center gap-2 bg-[#FFD700] text-[#1A1A1A] border-2 border-[#FFD700] rounded-full px-6 py-3 font-bold uppercase tracking-wide nb-press disabled:opacity-70"
                  >
                    {submitting ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} strokeWidth={2.5} />}
                    {submitting ? "Sending…" : `Send ${haveCount} / ${total} to Julija`}
                  </button>
                  <a
                    href={CALENDLY_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-transparent text-white border-2 border-white/60 rounded-full px-6 py-3 font-bold uppercase tracking-wide hover:bg-white hover:text-[#1A1A1A] transition-colors"
                  >
                    …or book a call <ArrowUpRight size={16} strokeWidth={2.5} />
                  </a>
                </div>
              </form>
            </>
          ) : (
            <div data-testid="checklist-sent-card" className="rounded-3xl bg-white text-[#1A1A1A] border-2 border-[#FFD700] p-8 nb-shadow-lg max-w-3xl">
              <CheckCircle2 size={40} strokeWidth={2.5} className="text-[#00C2B8]" />
              <h2 className="mt-4 font-display font-black text-3xl md:text-4xl tracking-tighter">
                Received. Thank you, {form.name.split(" ")[0] || "friend"}.
              </h2>
              <p className="mt-3 text-[#4A4A4A] font-medium">
                We’ve logged <strong>{haveCount}</strong> items you have and
                <strong> {missingCount}</strong> gaps to look at. Julija will
                reply within 1–2 business days with a tailored summary.
              </p>
              <p className="mt-4 text-sm font-mono uppercase tracking-widest text-[#4A4A4A]">
                Reply-to: {form.email}
              </p>
            </div>
          )}
        </div>
      </section>

      <style>{`
        .form-dark {
          background: rgba(255,255,255,0.06);
          border: 2px solid rgba(255,255,255,0.25);
          border-radius: 14px;
          padding: 12px 14px;
          color: #FFFFFF;
          font-weight: 600;
          font-family: 'DM Sans', sans-serif;
          outline: none;
          transition: all 150ms ease;
        }
        .form-dark::placeholder { color: rgba(255,255,255,0.45); font-weight: 500; }
        .form-dark:focus {
          border-color: #FFD700;
          background: rgba(255,215,0,0.08);
          box-shadow: 4px 4px 0 0 #FFD700;
          transform: translate(-1px, -1px);
        }
        @media print {
          [data-testid="site-header"], [data-testid="site-footer"],
          [data-testid="checklist-submit-section"] { display: none !important; }
        }
      `}</style>
    </div>
  );
}
